import { MarkdownText } from './MarkdownText';
import { cn } from '../../../lib/cn';
import {
  ActionBarPrimitive,
  AuiIf,
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
} from '@assistant-ui/react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckIcon,
  CopyIcon,
} from 'lucide-react';
import { type FC } from 'react';
import ChatReasoningTrace, {
  ChatReasoningGroup,
} from '../../shared/ChatReasoningTrace';

// Thread (root)

export interface ThreadProps {
  /** When true, the built-in composer is hidden so a custom composer can be
   *  rendered by the parent (e.g., the Edit-with-AI drawer's dual-popup one). */
  hideComposer?: boolean;
}

export const Thread: FC<ThreadProps> = ({ hideComposer = false }) => {
  return (
    <ThreadPrimitive.Root
      className="aui-root aui-thread-root flex h-full flex-col bg-white dark:bg-nightshade-900"
      style={{
        ['--thread-max-width' as string]: '100%',
      }}
    >
      <ThreadPrimitive.Viewport
        turnAnchor="top"
        className="aui-thread-viewport relative flex flex-1 flex-col overflow-x-auto overflow-y-scroll scroll-smooth px-4 pt-4"
      >
        <ThreadPrimitive.Messages
          components={{
            UserMessage,
            AssistantMessage,
          }}
        />

        {!hideComposer && (
          <ThreadPrimitive.ViewportFooter className="aui-thread-viewport-footer sticky bottom-0 mx-auto mt-auto flex w-full max-w-[var(--thread-max-width)] flex-col gap-4 overflow-visible bg-white dark:bg-nightshade-900 pb-4 pt-2">
            <ThreadScrollToBottom />
            <Composer />
          </ThreadPrimitive.ViewportFooter>
        )}
      </ThreadPrimitive.Viewport>
    </ThreadPrimitive.Root>
  );
};

// ScrollToBottom

const ThreadScrollToBottom: FC = () => {
  return (
    <ThreadPrimitive.ScrollToBottom asChild>
      <button
        className="absolute -top-12 z-10 self-center rounded-full border border-stone-200 dark:border-nightshade-700 bg-white dark:bg-nightshade-900 p-2 shadow-sm hover:bg-stone-50 dark:hover:bg-nightshade-800 disabled:invisible transition-colors"
        aria-label="Scroll to bottom"
      >
        <ArrowDownIcon className="size-4 text-stone-500 dark:text-nightshade-400" />
      </button>
    </ThreadPrimitive.ScrollToBottom>
  );
};

// Composer

const Composer: FC = () => {
  return (
    <ComposerPrimitive.Root className="aui-composer-root relative flex w-full flex-col rounded-[18px] bg-white/88 dark:bg-nightshade-900/88 backdrop-blur-md outline-none transition-all shadow-md hover:shadow-lg has-[textarea:focus-visible]:shadow-xl has-[textarea:focus-visible]:ring-[3px] has-[textarea:focus-visible]:ring-blueberry-100 has-[textarea:focus-visible]:border-blueberry-300 border border-white/50 dark:border-nightshade-700/50">
      <ComposerPrimitive.Input
        placeholder="Send a message…"
        className="aui-composer-input max-h-32 min-h-12 w-full resize-none bg-transparent px-4 pt-3 pb-2 text-sm outline-none placeholder:text-content-placeholder focus-visible:ring-0"
        rows={1}
        autoFocus
        aria-label="Message input"
      />
      <ComposerAction />
    </ComposerPrimitive.Root>
  );
};

const ComposerAction: FC = () => {
  return (
    <div className="relative mx-3 mb-3 flex items-center gap-1.5 flex-wrap">
      {/* Tools chip — neutral grey */}
      <button
        type="button"
        className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium bg-nightshade-100 dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-700 text-nightshade-600 dark:text-nightshade-300 hover:bg-nightshade-200 dark:hover:bg-nightshade-700 transition-colors"
      >
        ⚙ tools
      </button>

      {/* @ personas — ube/purple */}
      <span className="flex items-center gap-1 text-[10px]">
        <span className="px-1.5 py-0.5 rounded bg-ube-100 dark:bg-ube-900/30 border border-ube-200 dark:border-ube-800 text-ube-700 dark:text-ube-300 font-mono font-bold text-[11px]">@</span>
        <span className="text-content-tertiary">personas</span>
      </span>

      <span className="text-content-faint text-[10px]">·</span>

      {/* / skills — blueberry */}
      <span className="flex items-center gap-1 text-[10px]">
        <span className="px-1.5 py-0.5 rounded bg-blueberry-100 dark:bg-blueberry-900/30 border border-blueberry-200 dark:border-blueberry-800 text-blueberry-700 dark:text-blueberry-300 font-mono font-bold text-[11px]">/</span>
        <span className="text-content-tertiary">skills</span>
      </span>

      {/* spacer */}
      <div className="flex-1" />

      {/* Send button — matcha green */}
      <AuiIf condition={(s) => !s.thread.isRunning}>
        <ComposerPrimitive.Send asChild>
          <button
            type="submit"
            className="inline-flex size-8 items-center justify-center rounded-full bg-matcha-600 text-white hover:bg-matcha-700 active:scale-95 transition-all shadow-[0_2px_8px_rgba(5,185,64,0.35)] disabled:opacity-50 flex-shrink-0"
            aria-label="Send message"
          >
            <ArrowUpIcon className="size-4" />
          </button>
        </ComposerPrimitive.Send>
      </AuiIf>
    </div>
  );
};

// AssistantMessage

const AssistantMessage: FC = () => {
  return (
    <MessagePrimitive.Root
      className="aui-assistant-message-root relative mx-auto w-full max-w-[var(--thread-max-width)] py-3"
      data-role="assistant"
    >
      <div className="aui-assistant-message-content wrap-break-word px-2 text-stone-800 dark:text-nightshade-100 text-sm leading-relaxed">
        <MessagePrimitive.Parts
          components={{
            Text: MarkdownText,
            ReasoningGroup: ChatReasoningGroup,
          }}
        />
      </div>

      <div className="px-2">
        <ChatReasoningTrace />
      </div>

      <div className="aui-assistant-message-footer mt-1 ml-2 flex">
        <AssistantActionBar />
      </div>
    </MessagePrimitive.Root>
  );
};

// AssistantActionBar (copy only)

const AssistantActionBar: FC = () => {
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      autohideFloat="single-branch"
      className="aui-assistant-action-bar-root -ml-1 flex gap-1 text-stone-400 dark:text-nightshade-500 data-floating:absolute data-floating:rounded-md data-floating:border data-floating:border-stone-200 dark:data-floating:border-nightshade-700 data-floating:bg-white dark:data-floating:bg-nightshade-900 data-floating:p-1 data-floating:shadow-sm"
    >
      <ActionBarPrimitive.Copy asChild>
        <CopyButton />
      </ActionBarPrimitive.Copy>
    </ActionBarPrimitive.Root>
  );
};

const CopyButton: FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = (props) => {
  return (
    <button
      {...props}
      className={cn(
        'inline-flex size-7 items-center justify-center rounded hover:bg-stone-100 dark:hover:bg-nightshade-800 hover:text-stone-600 dark:hover:text-nightshade-300 transition-colors',
        props.className,
      )}
      aria-label="Copy message"
    >
      <AuiIf condition={(s) => s.message.isCopied}>
        <CheckIcon className="size-3.5" />
      </AuiIf>
      <AuiIf condition={(s) => !s.message.isCopied}>
        <CopyIcon className="size-3.5" />
      </AuiIf>
    </button>
  );
};

// UserMessage

const UserMessage: FC = () => {
  return (
    <MessagePrimitive.Root
      className="aui-user-message-root mx-auto grid w-full max-w-[var(--thread-max-width)] auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] content-start gap-y-2 px-2 py-3 [&:where(>*)]:col-start-2"
      data-role="user"
    >
      <div className="aui-user-message-content-wrapper relative col-start-2 min-w-0">
        <div className="aui-user-message-content wrap-break-word rounded-2xl bg-matcha-600 px-4 py-2.5 text-white text-sm">
          <MessagePrimitive.Parts />
        </div>
      </div>
    </MessagePrimitive.Root>
  );
};
